import { getSessionId, saveSessionId, debugSession } from "./sessionManager";

// Request tracking for debugging request order and timing
const requestTracker = {
  requests: [] as {
    id: string;
    path: string;
    startTime: number;
    endTime?: number;
    status?: number;
    order: number;
  }[],
  order: 0,
  active: 0,

  // Log the current request queue state
  logRequestQueue: function () {
    if (this.requests.length === 0) {
      console.log("📊 [REQUEST TRACKER] No requests have been logged yet");
      return;
    }

    console.group("📊 [REQUEST TRACKER] Current Request Queue");
    console.log(
      `Total requests: ${this.requests.length}, Active requests: ${this.active}`
    );

    // Group requests by path for easier analysis
    const requestsByPath = this.requests.reduce((acc, req) => {
      const path = req.path.split("?")[0]; // Remove query params for grouping
      if (!acc[path]) acc[path] = [];
      acc[path].push(req);
      return acc;
    }, {} as Record<string, typeof this.requests>);

    // Log requests by path
    Object.entries(requestsByPath).forEach(([path, reqs]) => {
      console.group(`Path: ${path} (${reqs.length} requests)`);
      reqs.forEach((req) => {
        const duration = req.endTime
          ? (req.endTime - req.startTime).toFixed(2)
          : "pending";
        const status = req.status ? `${req.status}` : "pending";
        console.log(
          `#${req.order} | ID: ${req.id} | Status: ${status} | Duration: ${duration}ms`
        );
      });
      console.groupEnd();
    });

    // Log the order of all requests
    console.group("Request Timeline");
    this.requests
      .sort((a, b) => a.order - b.order)
      .forEach((req) => {
        const duration = req.endTime
          ? (req.endTime - req.startTime).toFixed(2)
          : "pending";
        const status = req.status ? `${req.status}` : "pending";
        console.log(
          `#${req.order} | ${req.path.slice(0, 40)}${
            req.path.length > 40 ? "..." : ""
          } | Status: ${status} | Duration: ${duration}ms`
        );
      });
    console.groupEnd();

    console.groupEnd();
  },

  // Start tracking a request
  startRequest: function (id: string, path: string) {
    this.active++;
    this.order++;
    this.requests.push({
      id,
      path,
      startTime: performance.now(),
      order: this.order,
    });

    // Log active request count for understanding concurrent requests
    console.log(
      `🔄 [REQUEST TRACKER] Active requests: ${this.active} (Started: ${path})`
    );
  },

  // End tracking a request
  endRequest: function (id: string, status: number) {
    const req = this.requests.find((r) => r.id === id);
    if (req) {
      req.endTime = performance.now();
      req.status = status;
    }
    this.active--;

    // Log the full request queue every 5 requests or when all requests are complete
    if (this.requests.length % 5 === 0 || this.active === 0) {
      this.logRequestQueue();
    }
  },
};

// Initialize session debugging on module load
// We'll let the backend generate the session ID
(function initializeSessionDebug() {
  if (typeof window !== "undefined") {
    // Check if we already have a session ID
    const existingSessionId = getSessionId();
    if (existingSessionId) {
      console.log("🔑 Using existing session ID:", existingSessionId);
    } else {
      console.log(
        "⏳ No session ID found, waiting for backend to generate one"
      );
    }

    // Debug the session state
    debugSession();
  }
})();

// Get API URL from appropriate sources with improved environment detection
const API_URL = (() => {
  // Check if running in development mode (npm start)
  const isDevMode = process.env.NODE_ENV === "development";

  // When running locally with npm start, always use localhost:1337
  if (isDevMode) {
    console.log(
      "🛠️ Development mode detected, using localhost:1337 for Strapi"
    );
    return "http://localhost:1337";
  }

  // For production builds, use the environment variable
  if (process.env.REACT_APP_STRAPI_HOST) {
    return process.env.REACT_APP_STRAPI_HOST;
  }

  // Detect environment based on hostname as a fallback
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;

    // Local development
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:1337";
    }

    // Production environment (evefant.com)
    if (hostname.includes("evefant.com")) {
      return "https://cms.evefant.com";
    }
  }

  // Default fallback for production
  return "https://cms.evefant.com";
})();

// Get the API token for protected endpoints
const API_TOKEN = process.env.REACT_APP_STRAPI_TOKEN;

console.log("🔌 Strapi API URL:", API_URL);
console.log("🌎 Environment:", process.env.NODE_ENV);

/**
 * Determine if a path should use API token authentication
 * @param path - API endpoint path
 * @returns boolean indicating if token should be used
 */
function shouldUseToken(path: string): boolean {
  // List of paths that don't require token authentication (cart related)
  const publicPaths = [
    "carts/",
    "cart-items/",
    "cart-items/add",
    "grouped-product-items/",
    "delivery-methods/",
    "delivery-lead-time/",
  ];

  // Check if the path starts with any of the public paths
  return !publicPaths.some((publicPath) => path.startsWith(publicPath));
}

/**
 * Check if we're in a secure context (HTTPS)
 */
function isSecureContext(): boolean {
  if (typeof window !== "undefined") {
    return window.location.protocol === "https:";
  }
  // Default to true in production when checking server-side
  return process.env.NODE_ENV === "production";
}

/**
 * Perform an API request to Strapi backend.
 * @param path - API endpoint path
 * @param options - Request options
 */
export async function query(path: string, options: RequestInit = {}) {
  // Determine if this path requires a token
  const useToken = shouldUseToken(path);

  // Use a unique ID for this request to correlate logs
  const requestId = `req_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .substring(2, 7)}`;

  // Start tracking this request
  requestTracker.startRequest(requestId, path);

  console.log(`🔍 [DEBUG API ${requestId}] Starting request to ${path}`);

  // Check if we're in a secure context
  const isSecure = isSecureContext();
  console.log(
    `🔒 [DEBUG API ${requestId}] Secure context: ${isSecure ? "Yes" : "No"}`
  );

  // Configure fetch options properly for all environments
  const defaultOptions: RequestInit = {
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include", // Critical for session cookies
    mode: "cors", // Always use CORS
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    // Add sessionId and cartId from localStorage for easier testing
    if (typeof window !== "undefined") {
      // First try to get sessionId from localStorage
      const storedSessionId = getSessionId();
      if (storedSessionId && mergedOptions.headers) {
        (mergedOptions.headers as Record<string, string>)["x-session-id"] =
          storedSessionId;
        console.log(
          `📤 [DEBUG API ${requestId}] Using session ID in request:`,
          storedSessionId
        );
      } else {
        console.warn(
          `⚠️ [DEBUG API ${requestId}] No session ID found in localStorage`
        );
      }

      // Handle cartId
      const cartId = localStorage.getItem("cartId");
      if (cartId && mergedOptions.headers) {
        (mergedOptions.headers as Record<string, string>)["x-cart-id"] = cartId;
        console.log(
          `📤 [DEBUG API ${requestId}] Using cart ID in request:`,
          cartId
        );
      } else {
        console.log(
          `⚠️ [DEBUG API ${requestId}] No cart ID found in localStorage`
        );
      }
    }

    // Add API token if required by path
    if (useToken && API_TOKEN && mergedOptions.headers) {
      (mergedOptions.headers as Record<string, string>)[
        "Authorization"
      ] = `Bearer ${API_TOKEN}`;
    }

    // Add protocol information to help server detect secure connections
    if (mergedOptions.headers) {
      const protocol = isSecure ? "https" : "http";
      (mergedOptions.headers as Record<string, string>)["x-forwarded-proto"] =
        protocol;
      console.log(
        `🔍 [DEBUG API ${requestId}] Setting x-forwarded-proto: ${protocol}`
      );
    }

    const fullUrl = `${API_URL}/api/${path}`;

    // Log the full request for debugging
    console.log(`🔄 [DEBUG API ${requestId}] Fetching ${fullUrl}`, {
      method: mergedOptions.method || "GET",
      credentials: mergedOptions.credentials,
      mode: mergedOptions.mode,
      headers: mergedOptions.headers,
      withToken: useToken,
      secure: isSecure,
      body: options.body ? "(Body present)" : "(No body)",
    });

    if (options.body && typeof options.body === "string") {
      try {
        const parsedBody = JSON.parse(options.body);
        console.log(
          `🔍 [DEBUG API ${requestId}] Request body:`,
          JSON.stringify(parsedBody, null, 2)
        );
      } catch (e) {
        console.log(
          `🔍 [DEBUG API ${requestId}] Request body (non-JSON):`,
          options.body
        );
      }
    }

    const fetchStartTime = performance.now();
    const response = await fetch(fullUrl, mergedOptions);

    // Detect redirect (Strapi sends 301/302)
    if (response.status === 301 || response.status === 302) {
      const location = response.headers.get("Location");
      if (location) {
        // If the redirect is to an API endpoint, map it to the frontend route
        if (location.startsWith("/api/product/")) {
          const slug = location.replace("/api/product/", "");
          window.location.replace(`/p/${slug}`);
        } else if (location.startsWith("/api/blog-article/by-url/")) {
          const slug = location.replace("/api/blog-article/by-url/", "");
          window.location.replace(`/blog/${slug}`);
        } else if (location.startsWith("/api/categories/")) {
          const slug = location.replace("/api/categories/", "");
          window.location.replace(`/c/${slug}`);
        } else {
          // fallback: just use the location
          window.location.replace(location);
        }
        return;
      }
    }

    const fetchDuration = performance.now() - fetchStartTime;

    // Record request completion
    requestTracker.endRequest(requestId, response.status);

    console.log(
      `📥 [DEBUG API ${requestId}] Response received in ${fetchDuration.toFixed(
        2
      )}ms with status: ${response.status}`
    );

    // Debug: Log all response headers to see what's available
    console.log(`📥 [DEBUG API ${requestId}] Response headers:`);
    let allHeadersLog = "";
    response.headers.forEach((value, key) => {
      console.log(`   ${key}: ${value}`);
      allHeadersLog += `${key}: ${value}\n`;
    });

    // Try different case variations for the session ID header
    let sessionIdFromHeader =
      response.headers.get("X-Session-ID") ||
      response.headers.get("x-session-id") ||
      response.headers.get("X-Session-Id") ||
      response.headers.get("x-session-Id");

    if (sessionIdFromHeader) {
      // Save the session ID and log it
      console.log(
        `📥 [DEBUG API ${requestId}] Received session ID from header:`,
        sessionIdFromHeader
      );
      saveSessionId(sessionIdFromHeader);
    } else {
      console.warn(
        `⚠️ [DEBUG API ${requestId}] No X-Session-ID header in response`
      );
    }

    if (!response.ok) {
      console.log(
        `❌ [DEBUG API ${requestId}] Response not OK: ${response.status} ${response.statusText}`
      );

      const errorText = await response.text(); // Read as text first
      console.log(
        `❌ [DEBUG API ${requestId}] Error response body (raw): ${errorText}`
      );

      let errorData;
      try {
        errorData = JSON.parse(errorText); // Then try to parse as JSON
        console.log(`❌ [DEBUG API ${requestId}] Parsed error:`, errorData);
      } catch (e) {
        errorData = errorText; // Keep as text if not valid JSON
        console.log(
          `❌ [DEBUG API ${requestId}] Could not parse error as JSON`
        );
      }

      console.error(
        `❌ [DEBUG API ${requestId}] API error (${response.status}):`,
        errorData
      );
      throw new Error(
        `API error (${response.status}): ${
          typeof errorData === "string" ? errorData : JSON.stringify(errorData)
        }`
      );
    }

    if (response.status === 204) {
      console.log(
        `✅ [DEBUG API ${requestId}] Request successful with 204 No Content`
      );
      return { success: true };
    }

    try {
      const jsonData = await response.json();
      console.log(
        `✅ [DEBUG API ${requestId}] Request successful with data:`,
        jsonData
      );
      return jsonData;
    } catch (jsonError: any) {
      // Handle JSON parsing errors, which could be due to circular references
      if (
        jsonError &&
        jsonError.message &&
        typeof jsonError.message === "string" &&
        jsonError.message.includes("circular")
      ) {
        console.error(
          `❌ [DEBUG API ${requestId}] Circular reference detected in API response from ${path}`
        );
        // Return a simplified response
        return {
          error: "Circular reference detected",
          path,
          message:
            "The server response contained a circular structure that couldn't be parsed",
        };
      }
      throw jsonError;
    }
  } catch (error: any) {
    // Record request failure
    requestTracker.endRequest(requestId, 0); // 0 indicates error

    // Handle circular reference errors in the overall request
    if (
      error &&
      error.message &&
      typeof error.message === "string" &&
      error.message.includes("circular")
    ) {
      console.error(
        `❌ [DEBUG API ${requestId}] Circular reference detected when fetching ${path}`
      );
      return {
        error: "Circular reference detected",
        path,
        message: "The operation failed due to a circular structure in the data",
      };
    }

    // Provide more specific error messages for common issues
    if (error instanceof TypeError && error.message === "Failed to fetch") {
      console.error(`❌ [DEBUG API ${requestId}] CORS or network error when fetching ${path}. This could be due to:
      1. CORS configuration issues on the server
      2. Network connectivity problems
      3. Server is down or unreachable
      
      Check browser console network tab for more details.`);

      // Add additional debugging info
      console.error(`Debug info:
      - API URL: ${API_URL}
      - Secure context: ${isSecure}
      - Current origin: ${window.location.origin}
      - Headers sent: ${JSON.stringify(
        Object.keys(mergedOptions.headers || {})
      )}
      `);
    } else {
      console.error(
        `❌ [DEBUG API ${requestId}] Error fetching ${path}:`,
        error
      );
    }
    throw error;
  }
}

// Export the request tracker for use in other components if needed
export const apiRequestTracker = requestTracker;

// Add a new function to log the complete request history
export function logAPIRequestHistory() {
  console.log("🔍 [API REQUEST HISTORY] Logging complete request history");
  requestTracker.logRequestQueue();
}

// Add a simple throttling mechanism to prevent excessive requests
const throttleMap = new Map();
const THROTTLE_LIMIT = 10; // requests
const THROTTLE_INTERVAL = 1000; // milliseconds

export function isThrottled(key: string): boolean {
  const now = Date.now();
  const timestamps = throttleMap.get(key) || [];
  const recentTimestamps = timestamps.filter(
    (t: number) => now - t < THROTTLE_INTERVAL
  );

  if (recentTimestamps.length >= THROTTLE_LIMIT) {
    return true;
  }

  throttleMap.set(key, [...recentTimestamps, now]);
  return false;
}
